import moment from "moment-timezone/builds/moment-timezone-with-data-10-year-range.js";
import React, { useEffect, useRef, useState } from "react";
import Toast from "react-native-toast-message";
import { DeviceEventEmitter, Platform, Pressable, View } from "react-native";
import tw from "twrnc";
import { Ionicons } from "@expo/vector-icons";
import { VideoView, useVideoPlayer } from "expo-video";
import AppActivityIndicator from "./AppActivityIndicator";
import AppText from "./AppText";
import AppTitle from "./AppTitle";
import { TrashCanButton } from "./Buttons";
import EventVideoUpload from "./EventVideoUpload";
import FormSubtitle from "./FormSubtitle";
import { deleteEventChunk } from "../http/events";
import { useActionSheet } from "@/components/hooks/useActionSheet";
import { t } from "@/localization";
import UploadV2Notice from "../uploadV2/UploadV2Notice";
import {
  intentForUpload,
  lifecyclePhaseFor,
  manageVideoLayout,
  reloadingPlaylist,
  resultCardShown,
  showsResultCard,
} from "../uploadV2/presentation";
import { useUploadV2 } from "../uploadV2/useUploadV2";
import { useTeamAccess } from "../teams/useWorkspace";

const VIDEO_CDN_BASE = "https://eventlivevids.b-cdn.net";
const EXT_STREAM_INF_PREFIX = "#EXT-X-STREAM-INF:";
const EXT_MAP_PREFIX = "#EXT-X-MAP:";
const EXTINF_PREFIX = "#EXTINF:";

const splitPlaylistLines = (playlistText = "") =>
  playlistText
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

const parseAttributeList = (input = "") => {
  const attrs = {};
  const attributeRegex = /([A-Z0-9-]+)=("(?:[^"\\]|\\.)*"|[^,]*)/gi;
  let match = attributeRegex.exec(input);

  while (match) {
    const key = match[1];
    const rawValue = match[2] || "";
    attrs[key] =
      rawValue.startsWith('"') && rawValue.endsWith('"')
        ? rawValue.slice(1, -1)
        : rawValue;
    match = attributeRegex.exec(input);
  }

  return attrs;
};

const withCacheBuster = (inputUrl, cacheBuster) => {
  if (!inputUrl) return inputUrl;

  try {
    const url = new URL(inputUrl);
    if (cacheBuster) {
      url.searchParams.set("v", String(cacheBuster));
    }
    return url.toString();
  } catch {
    console.error("Error parsing URL", inputUrl);
    return inputUrl;
  }
};

const parseComparableVersion = (value) => {
  if (value == null) return null;
  if (typeof value === "number" && Number.isFinite(value)) return value;

  const normalized = String(value).trim();
  if (!normalized) return null;

  if (/^\d+(\.\d+)?$/.test(normalized)) {
    const numericValue = Number(normalized);
    return Number.isFinite(numericValue) ? numericValue : null;
  }

  const parsedDate = Date.parse(normalized);
  return Number.isFinite(parsedDate) ? parsedDate : null;
};

const getLatestDatetimeUploaded = (eventValue, overrideValue) => {
  if (!overrideValue) return eventValue ?? null;
  if (!eventValue) return overrideValue;

  const eventVersion = parseComparableVersion(eventValue);
  const overrideVersion = parseComparableVersion(overrideValue);

  if (eventVersion != null && overrideVersion != null) {
    return eventVersion >= overrideVersion ? eventValue : overrideValue;
  }

  return String(eventValue) === String(overrideValue)
    ? eventValue
    : overrideValue;
};

const hasOwn = (object, key) =>
  Object.prototype.hasOwnProperty.call(object || {}, key);

const hasStreamHistory = (event) => {
  if (!event) return false;
  if (
    event.datetime_broadcast_started ||
    event.datetime_broadcast_stopped ||
    event.datetime_broadcast_finished ||
    event.playlist_url
  ) {
    return true;
  }

  return ["live", "paused", "finished"].includes(event.event_status);
};

const resolveUrl = (baseUrl, uri, cacheBuster) => {
  if (!uri) return null;
  try {
    const resolved = new URL(uri, baseUrl).toString();
    return withCacheBuster(resolved, cacheBuster);
  } catch {
    return null;
  }
};

const parseResolutionWidth = (resolution = "") => {
  const [width] = resolution.split("x");
  const parsedWidth = Number.parseInt(width, 10);
  return Number.isFinite(parsedWidth) ? parsedWidth : 0;
};

const parseMasterPlaylist = (playlistText, masterUrl, cacheBuster) => {
  const lines = splitPlaylistLines(playlistText);
  const variants = [];

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index];
    if (!line.startsWith(EXT_STREAM_INF_PREFIX)) continue;

    const attrs = parseAttributeList(line.slice(EXT_STREAM_INF_PREFIX.length));

    let uriLine = null;
    for (let nextIndex = index + 1; nextIndex < lines.length; nextIndex += 1) {
      const nextLine = lines[nextIndex];
      if (nextLine.startsWith("#")) continue;
      uriLine = nextLine;
      break;
    }

    if (!uriLine) continue;

    variants.push({
      url: resolveUrl(masterUrl, uriLine, cacheBuster),
      bandwidth: Number.parseInt(attrs.BANDWIDTH, 10) || 0,
      width: parseResolutionWidth(attrs.RESOLUTION),
    });
  }

  if (!variants.length) {
    return withCacheBuster(masterUrl, cacheBuster);
  }

  variants.sort(
    (left, right) =>
      right.bandwidth - left.bandwidth || right.width - left.width
  );

  return variants[0].url || withCacheBuster(masterUrl, cacheBuster);
};

const getMapUri = (line) => {
  const attrs = parseAttributeList(line.slice(EXT_MAP_PREFIX.length));
  return attrs.URI || null;
};

const normalizeFilename = (fileName = "") => fileName.split("?")[0];

const parseTimestampFromFilename = (fileName) => {
  const match = normalizeFilename(fileName).match(
    /_(\d{4}-\d{2}-\d{2}-\d{2}-\d{2}-\d{2})(?:_|\.|$)/
  );
  if (!match?.[1]) return null;

  const timestamp = moment.utc(match[1], "YYYY-MM-DD-HH-mm-ss", true);
  if (!timestamp.isValid()) return null;

  return timestamp;
};

const formatDuration = (secondsTotal = 0) => {
  const roundedSeconds = Math.max(0, Math.round(secondsTotal));
  const hours = Math.floor(roundedSeconds / 3600);
  const minutes = Math.floor((roundedSeconds % 3600) / 60);
  const seconds = roundedSeconds % 60;

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, "0")}:${String(
      seconds
    ).padStart(2, "0")}`;
  }

  return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

const toFragmentModel = ({
  mediaFileName,
  mediaPlaylistUrl,
  cacheBuster,
  durationSeconds,
  order,
}) => {
  const normalizedMediaName = normalizeFilename(mediaFileName);
  if (!normalizedMediaName) return null;

  const mapToPlaylistName = normalizedMediaName.replace(/\.[^.]+$/, ".m3u8");
  const timestamp = parseTimestampFromFilename(normalizedMediaName);
  const localTimestamp = timestamp ? timestamp.local() : null;

  return {
    id: `${normalizedMediaName}:${order}`,
    order,
    sortTimestamp: timestamp ? timestamp.valueOf() : null,
    mediaFileName: normalizedMediaName,
    playlistFileName: mapToPlaylistName,
    mediaUrl: resolveUrl(mediaPlaylistUrl, normalizedMediaName, cacheBuster),
    playlistUrl: resolveUrl(mediaPlaylistUrl, mapToPlaylistName, cacheBuster),
    durationSeconds,
    displayDate:
      (localTimestamp && localTimestamp.format(t("ddd, MMM D, YYYY"))) ||
      t("Video fragment {number}", { number: order + 1 }),
    displayTime: localTimestamp ? localTimestamp.format(t("h:mm A z")) : null,
  };
};

const parseMediaPlaylistFragments = ({
  playlistText,
  mediaPlaylistUrl,
  cacheBuster,
}) => {
  const lines = splitPlaylistLines(playlistText);
  const rawFragments = [];

  let currentMediaFileName = null;
  let currentDurationSeconds = 0;
  let fallbackOrder = 0;

  const flushCurrent = () => {
    if (!currentMediaFileName) return;

    const fragment = toFragmentModel({
      mediaFileName: currentMediaFileName,
      mediaPlaylistUrl,
      cacheBuster,
      durationSeconds: currentDurationSeconds,
      order: fallbackOrder,
    });

    if (fragment) {
      rawFragments.push(fragment);
      fallbackOrder += 1;
    }

    currentMediaFileName = null;
    currentDurationSeconds = 0;
  };

  lines.forEach((line) => {
    if (line.startsWith(EXT_MAP_PREFIX)) {
      const mapUri = getMapUri(line);
      if (!mapUri) return;
      flushCurrent();
      currentMediaFileName = mapUri;
      return;
    }

    if (line.startsWith(EXTINF_PREFIX)) {
      const durationRaw = line.slice(EXTINF_PREFIX.length).split(",")[0];
      const parsedDuration = Number.parseFloat(durationRaw);
      if (Number.isFinite(parsedDuration)) {
        currentDurationSeconds += parsedDuration;
      }
      return;
    }

    if (line.startsWith("#")) return;

    if (!currentMediaFileName) {
      currentMediaFileName = line;
    }
  });

  flushCurrent();

  const dedupedByFileName = new Map();
  rawFragments.forEach((fragment) => {
    const existing = dedupedByFileName.get(fragment.mediaFileName);
    if (!existing || fragment.durationSeconds > existing.durationSeconds) {
      dedupedByFileName.set(fragment.mediaFileName, fragment);
    }
  });

  return Array.from(dedupedByFileName.values());
};

const fetchPlaylistText = async (url) => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Could not load playlist (${response.status})`);
  }
  return response.text();
};

const loadFragmentsFromPlaylists = async ({ masterUrl, cacheBuster }) => {
  const masterPlaylistText = await fetchPlaylistText(masterUrl);
  const mediaPlaylistUrl = parseMasterPlaylist(
    masterPlaylistText,
    masterUrl,
    cacheBuster
  );
  const mediaPlaylistText =
    mediaPlaylistUrl === masterUrl
      ? masterPlaylistText
      : await fetchPlaylistText(mediaPlaylistUrl);

  return parseMediaPlaylistFragments({
    playlistText: mediaPlaylistText,
    mediaPlaylistUrl,
    cacheBuster,
  });
};

const NativeFragmentPlayer = ({ playableUrl }) => {
  const player = useVideoPlayer(playableUrl);

  return (
    <View
      style={tw`rounded-md overflow-hidden border border-[#d7d7d7] bg-black`}
    >
      <VideoView
        player={player}
        style={{ width: "100%", aspectRatio: 16 / 9 }}
        contentFit="contain"
        allowsFullscreen
        allowsPictureInPicture
      />
    </View>
  );
};

const FragmentPlayer = ({ fragment }) => {
  const playableUrl = fragment.playlistUrl || fragment.mediaUrl;
  if (!playableUrl) return null;

  if (Platform.OS === "web") {
    const webSrc = fragment.mediaUrl || fragment.playlistUrl;
    return (
      <View style={tw`rounded-md overflow-hidden border border-[#d7d7d7]`}>
        <video
          controls
          preload="metadata"
          src={webSrc}
          style={{
            width: "100%",
            maxHeight: 420,
            backgroundColor: "black",
            display: "block",
          }}
        />
      </View>
    );
  }

  return <NativeFragmentPlayer playableUrl={playableUrl} />;
};

const isSameUploadIntent = (left, right) =>
  left?.position === right?.position &&
  left?.afterFragmentId === right?.afterFragmentId;

const getUploadMutationMeta = (intent, fragments) => {
  if (!intent) return { mode: null, insertIndex: null };
  if (intent.position === "beginning") {
    return { mode: "prepend", insertIndex: null };
  }
  if (intent.position === "end") {
    return { mode: "append", insertIndex: null };
  }
  if (intent.position === "replace") {
    return { mode: "replace", insertIndex: null };
  }
  if (intent.position === "between") {
    const afterIndex = fragments.findIndex(
      (fragment) => fragment.id === intent.afterFragmentId
    );
    return {
      mode: "insert",
      insertIndex: afterIndex >= 0 ? afterIndex + 1 : null,
    };
  }

  return { mode: null, insertIndex: null };
};

const UploadPositionButton = ({
  text,
  onPress,
  icon = "add-circle-outline",
  active,
  disabled,
}) => {
  return (
    <Pressable
      disabled={disabled}
      onPress={onPress}
      style={({ pressed }) => [
        tw`h-9 px-2.5 rounded border flex-row items-center justify-center`,
        active ? tw`border-[#3F5B7A] bg-[#eef4fb]` : tw`border-[#cfd8e3]`,
        disabled ? tw`opacity-50` : null,
        pressed ? tw`bg-[#eef4fb]` : null,
      ]}
    >
      <Ionicons name={icon} size={16} color="#3F5B7A" style={tw`mr-1`} />
      <AppText style={tw`text-[12px] font-semibold text-[#3F5B7A]`}>
        {text}
      </AppText>
    </Pressable>
  );
};

const VideoFragmentsList = ({
  fragments,
  canDestroy = true,
  canDeleteLastFragment,
  onDeleteFragmentPress,
  onUploadPositionPress,
  isUploadIntentActive,
  isUploadLocked,
  activeUploadIntent,
  renderUploadArea,
}) => {
  const [expandedId, setExpandedId] = useState(null);
  const canDeleteFragments =
    canDestroy && (fragments.length > 1 || canDeleteLastFragment);

  useEffect(() => {
    if (
      expandedId &&
      !fragments.some((fragment) => fragment.id === expandedId)
    ) {
      setExpandedId(null);
    }
  }, [expandedId, fragments]);

  useEffect(() => {
    if (
      activeUploadIntent?.position === "between" &&
      activeUploadIntent.afterFragmentId
    ) {
      setExpandedId(activeUploadIntent.afterFragmentId);
    }
  }, [activeUploadIntent]);

  return (
    <View style={tw`mb-4`}>
      {(() => {
        const beginningIntent = { position: "beginning" };
        const endIntent = { position: "end" };
        const replaceIntent = { position: "replace" };
        return (
          <>
            <View style={tw`flex-row mb-2`}>
              <View style={tw`flex-1 mr-1`}>
                <UploadPositionButton
                  text={t("Upload to Beginning")}
                  icon="arrow-up-circle-outline"
                  active={isUploadIntentActive?.(beginningIntent)}
                  disabled={
                    isUploadLocked && !isUploadIntentActive?.(beginningIntent)
                  }
                  onPress={() => onUploadPositionPress?.(beginningIntent)}
                />
              </View>
              <View style={tw`flex-1 ml-1`}>
                <UploadPositionButton
                  text={t("Upload to End")}
                  icon="arrow-down-circle-outline"
                  active={isUploadIntentActive?.(endIntent)}
                  disabled={
                    isUploadLocked && !isUploadIntentActive?.(endIntent)
                  }
                  onPress={() => onUploadPositionPress?.(endIntent)}
                />
              </View>
            </View>
            {canDestroy && (
              <View style={tw`mb-2`}>
                <UploadPositionButton
                  text={t("Replace All Video")}
                  icon="swap-horizontal-outline"
                  active={isUploadIntentActive?.(replaceIntent)}
                  disabled={
                    isUploadLocked && !isUploadIntentActive?.(replaceIntent)
                  }
                  onPress={() => onUploadPositionPress?.(replaceIntent)}
                />
              </View>
            )}
            {renderUploadArea?.(beginningIntent)}
            {renderUploadArea?.(endIntent)}
            {renderUploadArea?.(replaceIntent)}
          </>
        );
      })()}

      <View style={tw`rounded-lg border border-[#d7d7d7] overflow-hidden`}>
        {fragments.map((fragment, index) => {
          const expanded = expandedId === fragment.id;
          const isLast = index === fragments.length - 1;
          const nextFragment = isLast ? null : fragments[index + 1];
          const afterIntent = {
            position: "between",
            afterFragmentId: fragment.id,
            beforeFragmentId: nextFragment?.id || null,
          };

          return (
            <View
              key={fragment.id}
              style={isLast ? null : tw`border-b border-[#e9e9e9]`}
            >
              <Pressable
                onPress={() => {
                  const willCollapse = expandedId === fragment.id;
                  const lockedRow =
                    isUploadLocked &&
                    activeUploadIntent?.position === "between" &&
                    activeUploadIntent?.afterFragmentId === fragment.id;
                  if (lockedRow) return;
                  if (willCollapse && isUploadIntentActive?.(afterIntent)) {
                    onUploadPositionPress?.(afterIntent);
                  }
                  setExpandedId((current) =>
                    current === fragment.id ? null : fragment.id
                  );
                }}
                style={({ pressed }) => [
                  tw`px-3 py-3`,
                  pressed ? tw`bg-[#f7f9fb]` : tw`bg-white`,
                ]}
              >
                <View style={tw`flex-row items-center justify-between`}>
                  <View style={tw`flex-row items-center flex-1 pr-3`}>
                    <Ionicons
                      name={expanded ? "chevron-down" : "chevron-forward"}
                      size={18}
                      color="#6f6f6f"
                      style={tw`mr-1`}
                    />
                    <View style={tw`flex-1`}>
                      <AppText style={tw`text-[14px] font-semibold`}>
                        {fragment.displayDate}
                      </AppText>
                      {!!fragment.displayTime && (
                        <AppText style={tw`text-[12px] text-[#6f6f6f] mt-0.5`}>
                          {fragment.displayTime}
                        </AppText>
                      )}
                    </View>
                  </View>

                  <View style={tw`items-end`}>
                    <AppText style={tw`text-[13px] font-semibold`}>
                      {formatDuration(fragment.durationSeconds)}
                    </AppText>
                  </View>
                </View>
              </Pressable>

              {expanded && (
                <View style={tw`px-3 pb-3 bg-white`}>
                  <View style={tw`flex-row items-center justify-between mb-2`}>
                    <AppText style={tw`text-[12px] text-[#6f6f6f]`}>
                      {t("Total length: {duration}", {
                        duration: formatDuration(fragment.durationSeconds),
                      })}
                    </AppText>
                    {canDeleteFragments ? (
                      <TrashCanButton
                        style={tw`relative top-0 right-0 h-[26px] w-[28px]`}
                        onPress={() =>
                          onDeleteFragmentPress?.({ fragment, index })
                        }
                      />
                    ) : null}
                  </View>
                  <FragmentPlayer fragment={fragment} />
                  {!isLast && (
                    <View style={tw`mt-3`}>
                      {!isUploadIntentActive?.(afterIntent) && (
                        <UploadPositionButton
                          text={t("Upload After")}
                          active={false}
                          disabled={
                            isUploadLocked &&
                            !isUploadIntentActive?.(afterIntent)
                          }
                          onPress={() => onUploadPositionPress?.(afterIntent)}
                        />
                      )}
                      {renderUploadArea?.(afterIntent)}
                    </View>
                  )}
                </View>
              )}
            </View>
          );
        })}
      </View>
    </View>
  );
};

const ManageVideoForm = ({ event, loading = false }) => {
  const { showActionSheetWithOptions } = useActionSheet();
  // Teams: adding video is for editors; deleting or replacing it, admins.
  const team = useTeamAccess();
  const canDestroy = team.can("deleteRecording", event);
  const [playlistState, setPlaylistState] = useState({
    loading: Boolean(event?.stream_key && event?.datetime_uploaded),
    resolved: !event?.stream_key || !event?.datetime_uploaded,
    error: null,
    fragments: [],
  });
  const [eventOverride, setEventOverride] = useState(null);
  const [uploadIntent, setUploadIntent] = useState(null);
  const [uploadLifecycle, setUploadLifecycle] = useState(null);
  const keepUploadIntentOnClose = useRef(false);
  const [deletingFragmentId, setDeletingFragmentId] = useState(null);
  const [datetimeUploadedOverride, setDatetimeUploadedOverride] =
    useState(null);
  const [playlistRefreshNonce, setPlaylistRefreshNonce] = useState(0);
  // Upload Service v2 (EXPO_PUBLIC_UPLOAD_V2; web): uploads run in an
  // app-wide manager, so this screen follows its state instead of the
  // card's callbacks, including after leaving and coming back.
  const uploadV2 = useUploadV2(event?.slug, { event });
  const uploadV2Phase = uploadV2.available
    ? lifecyclePhaseFor(uploadV2.state)
    : null;
  // v2: "Your video is ready" stays in its upload area until it's closed,
  // another upload spot is picked or the screen goes away, through the event
  // and playlist reloads that show the new video (manageVideoLayout).
  const holdReadyCard = uploadV2.available && uploadV2.state.status === "done";
  const holdReadyCardRef = useRef(holdReadyCard);
  holdReadyCardRef.current = holdReadyCard;

  useEffect(() => {
    setDeletingFragmentId(null);
    setEventOverride(null);
    setDatetimeUploadedOverride(null);
    setPlaylistState({
      loading: Boolean(event?.stream_key && event?.datetime_uploaded),
      resolved: !event?.stream_key || !event?.datetime_uploaded,
      error: null,
      fragments: [],
    });
  }, [event?.slug]);

  const eventOverrideDatetimeUploaded = hasOwn(
    eventOverride,
    "datetime_uploaded"
  )
    ? eventOverride.datetime_uploaded
    : datetimeUploadedOverride;
  const effectiveDatetimeUploaded =
    hasOwn(eventOverride, "datetime_uploaded") &&
    eventOverride.datetime_uploaded == null
      ? null
      : getLatestDatetimeUploaded(
          event?.datetime_uploaded,
          eventOverrideDatetimeUploaded
        );
  const effectiveEvent = event
    ? {
        ...event,
        ...(eventOverride || {}),
        datetime_uploaded: effectiveDatetimeUploaded,
      }
    : event;

  const streamKey = effectiveEvent?.stream_key;
  const cacheBuster = effectiveEvent?.datetime_uploaded
    ? String(effectiveEvent.datetime_uploaded)
    : null;
  const safeStreamKey = streamKey ? encodeURIComponent(streamKey) : null;
  const masterPlaylistUrl =
    safeStreamKey && effectiveDatetimeUploaded
      ? withCacheBuster(
          `${VIDEO_CDN_BASE}/${safeStreamKey}/${safeStreamKey}.m3u8`,
          cacheBuster
        )
      : null;

  useEffect(() => {
    let isCancelled = false;

    const loadFragments = async () => {
      if (!masterPlaylistUrl) {
        setPlaylistState({
          loading: false,
          resolved: true,
          error: null,
          fragments: [],
        });
        return;
      }

      setPlaylistState((current) =>
        reloadingPlaylist(current, holdReadyCardRef.current)
      );

      try {
        const fragments = await loadFragmentsFromPlaylists({
          masterUrl: masterPlaylistUrl,
          cacheBuster,
        });

        if (isCancelled) return;
        setPlaylistState({
          loading: false,
          resolved: true,
          error: null,
          fragments,
        });
      } catch (error) {
        if (isCancelled) return;
        console.log("[ManageVideo] Could not load fragments", error);
        setPlaylistState({
          loading: false,
          resolved: true,
          error,
          fragments: [],
        });
      }
    };

    loadFragments();

    return () => {
      isCancelled = true;
    };
  }, [masterPlaylistUrl, cacheBuster, playlistRefreshNonce]);

  const hasFragments = playlistState.fragments.length > 0;
  const canDeleteLastFragment = !hasStreamHistory(effectiveEvent);
  const layout = manageVideoLayout({
    loading,
    deleting: Boolean(deletingFragmentId),
    streamKey,
    playlist: playlistState,
    holdReadyCard,
  });
  const showUploadFallback = layout.showFallback;

  // "Upload cancelled" is on screen: the native uploader can let its record
  // of the cancel go (the card stays until it's closed).
  const cancelledCardShown =
    uploadV2.available &&
    uploadV2.state.status === "cancelled" &&
    resultCardShown(uploadV2.state, {
      showFallback: layout.showFallback,
      showList: layout.showList,
      uploadAreaOpen: Boolean(uploadIntent),
    });
  const { markSeen: markUploadV2Seen } = uploadV2;
  useEffect(() => {
    if (cancelledCardShown) markUploadV2Seen();
  }, [cancelledCardShown, markUploadV2Seen]);
  const isUploadLocked =
    Boolean(uploadLifecycle) || Boolean(deletingFragmentId);

  const onUploadPositionPress = (payload) => {
    if (isUploadLocked) {
      if (!isSameUploadIntent(uploadIntent, payload)) return;
      return;
    }
    // "Your video is ready" / "Upload cancelled" belong to the upload that
    // just ended: picking an upload spot closes them (and keeps the spot just
    // picked open).
    if (uploadV2.available && showsResultCard(uploadV2.state)) {
      keepUploadIntentOnClose.current = true;
      uploadV2.dismiss();
    }
    setUploadIntent((current) =>
      isSameUploadIntent(current, payload) ? null : payload
    );
  };

  useEffect(() => {
    if (!hasFragments && uploadIntent) {
      setUploadIntent(null);
    }
  }, [hasFragments, uploadIntent]);

  useEffect(() => {
    if (
      uploadLifecycle?.phase === "waiting_for_false" &&
      event?.vod_converted === false
    ) {
      setUploadLifecycle((current) =>
        current ? { ...current, phase: "converting" } : current
      );
    }
  }, [event?.vod_converted, uploadLifecycle]);

  useEffect(() => {
    if (
      uploadLifecycle?.phase === "converting" &&
      !uploadLifecycle.v2 &&
      event?.vod_converted === true
    ) {
      setUploadLifecycle(null);
      setUploadIntent(null);
    }
  }, [event?.vod_converted, uploadLifecycle]);

  // v2: lock the screen while the upload or its conversion is in progress
  // (the server queues the conversion; vod_converted=false never arrives
  // through eventUpdated), and put the upload area back where it started.
  useEffect(() => {
    if (!uploadV2.available) return;
    if (!uploadV2Phase) {
      setUploadLifecycle((current) => (current?.v2 ? null : current));
      // "Your video is ready" and "Upload cancelled" show in the upload area
      // the upload was meant for, also when the screen opens with them (e.g.
      // a cancel from Android's notification found at the app's start); a
      // first upload's moves into the list once the list exists.
      if (
        showsResultCard(uploadV2.state) &&
        playlistState.resolved &&
        playlistState.fragments.length > 0
      ) {
        const readyIntent = intentForUpload(
          uploadV2.state,
          playlistState.fragments
        );
        setUploadIntent((current) => current || readyIntent);
      }
      return;
    }
    if (!playlistState.resolved) return;
    const restoredIntent =
      playlistState.fragments.length > 0
        ? intentForUpload(uploadV2.state, playlistState.fragments)
        : null;
    setUploadLifecycle((current) => {
      if (current?.v2 && current.phase === uploadV2Phase) return current;
      return {
        intent: current ? current.intent : restoredIntent,
        phase: uploadV2Phase,
        v2: true,
      };
    });
    if (restoredIntent) {
      setUploadIntent((current) => current || restoredIntent);
    }
  }, [
    uploadV2.available,
    uploadV2Phase,
    uploadV2.state,
    holdReadyCard,
    playlistState.resolved,
    playlistState.fragments,
  ]);

  // v2: finished (status poll or eventUpdated): the card says "Your video is
  // ready" until it's closed; or the conversion failed (the notice above the
  // list says so).
  const uploadV2Status = uploadV2.state.status;
  const uploadV2ErrorStage = uploadV2.state.errorStage;
  const uploadV2DoneVia = uploadV2.state.doneVia;
  const previousUploadV2Status = useRef(uploadV2Status);
  useEffect(() => {
    const previous = previousUploadV2Status.current;
    previousUploadV2Status.current = uploadV2Status;
    if (!uploadV2.available) return;
    if (uploadV2Status === "done") {
      // Without an eventUpdated for the new video, reload the event so the
      // fragment list shows it.
      if (previous !== "done" && uploadV2DoneVia !== "event") {
        DeviceEventEmitter.emit("refresh_event");
      }
    } else if (
      (previous === "done" || previous === "cancelled") &&
      uploadV2Status === "idle"
    ) {
      // "Your video is ready" / "Upload cancelled" was closed: close its
      // upload area too.
      if (!keepUploadIntentOnClose.current) setUploadIntent(null);
      keepUploadIntentOnClose.current = false;
    } else if (
      uploadV2Status === "failed" &&
      uploadV2ErrorStage === "conversion"
    ) {
      setUploadIntent(null);
    }
  }, [uploadV2.available, uploadV2Status, uploadV2ErrorStage, uploadV2DoneVia]);

  const isUploadIntentActive = (intent) =>
    isSameUploadIntent(uploadIntent, intent);

  const getUploadActionLabel = (intent) => {
    if (!intent) return t("Upload video");
    if (intent.position === "beginning") return t("Upload to Beginning");
    if (intent.position === "end") return t("Upload to End");
    if (intent.position === "between") return t("Upload After");
    return t("Upload video");
  };

  const renderUploadArea = (intent) => {
    if (!isUploadIntentActive(intent)) return null;

    const actionLabel = getUploadActionLabel(intent);
    const { mode, insertIndex } = getUploadMutationMeta(
      intent,
      playlistState.fragments
    );
    return (
      <View style={tw`mt-2`}>
        <EventVideoUpload
          event={effectiveEvent}
          uploadButtonText={actionLabel}
          hideSubtitle={true}
          hideNotifyGuests={true}
          forceShowUploadForm={!isUploadLocked}
          uploadMode={mode}
          insertIndex={insertIndex}
          uploadIntent={intent}
          onUploadStart={() => {
            setEventOverride(null);
            setUploadIntent(intent);
            setUploadLifecycle({
              intent,
              phase: "uploading",
              ...(uploadV2.available ? { v2: true } : {}),
            });
          }}
          onUploadQueued={() => {
            setUploadLifecycle({ intent, phase: "waiting_for_false" });
          }}
          onUploadError={() => {
            setUploadLifecycle(null);
          }}
        />
      </View>
    );
  };

  const onDeleteFragmentPress = ({ fragment, index }) => {
    if (
      deletingFragmentId ||
      (playlistState.fragments.length <= 1 && !canDeleteLastFragment)
    ) {
      return;
    }

    const isDeletingLastFragment = playlistState.fragments.length <= 1;

    showActionSheetWithOptions(
      {
        options: [t("Delete"), t("Cancel")],
        cancelButtonIndex: 1,
        destructiveButtonIndex: 0,
        title: isDeletingLastFragment
          ? t("Delete this uploaded video?")
          : t("Delete this video fragment?"),
      },
      async (buttonIndex) => {
        if (buttonIndex !== 0) return;
        // A deletion ends "Your video is ready" (like picking another upload
        // spot), so the reload after it doesn't keep the old list on screen.
        if (holdReadyCardRef.current) uploadV2.dismiss();

        try {
          setDeletingFragmentId(fragment.id);
          const updatedEvent = await deleteEventChunk(event.slug, index);
          setEventOverride(updatedEvent || null);

          if (updatedEvent?.datetime_uploaded) {
            setDatetimeUploadedOverride(updatedEvent.datetime_uploaded);
          } else {
            setDatetimeUploadedOverride(null);
          }

          if (isDeletingLastFragment) {
            setPlaylistState({
              loading: false,
              resolved: true,
              error: null,
              fragments: [],
            });
            setUploadIntent(null);
            setUploadLifecycle(null);
          }

          setPlaylistRefreshNonce((current) => current + 1);
          DeviceEventEmitter.emit("refresh_event");
          Toast.show({
            text1: t("Video fragment deleted"),
          });
        } catch (error) {
          console.log("[ManageVideo] Could not delete fragment", error);
          Toast.show({
            text1: t("Could not delete video fragment"),
            type: "error",
          });
        } finally {
          setDeletingFragmentId(null);
        }
      }
    );
  };

  return (
    <>
      <AppTitle text={t("Manage Video")} style={tw`mb-4`} />

      {uploadV2.available && <UploadV2Notice event={effectiveEvent} />}

      {!!layout.spinner && (
        <View style={tw`mb-4`}>
          <AppActivityIndicator />
          <FormSubtitle style={tw`mt-2`}>
            {layout.spinner === "event"
              ? t("Loading event video...")
              : layout.spinner === "deleting"
              ? t("Deleting video fragment...")
              : t("Loading uploaded video fragments...")}
          </FormSubtitle>
        </View>
      )}

      {layout.showList && (
        <>
          <VideoFragmentsList
            fragments={playlistState.fragments}
            canDestroy={canDestroy}
            canDeleteLastFragment={canDeleteLastFragment}
            onUploadPositionPress={onUploadPositionPress}
            onDeleteFragmentPress={onDeleteFragmentPress}
            isUploadIntentActive={isUploadIntentActive}
            isUploadLocked={isUploadLocked}
            activeUploadIntent={uploadLifecycle?.intent || uploadIntent}
            renderUploadArea={renderUploadArea}
          />
        </>
      )}

      {showUploadFallback && (
        <>
          <EventVideoUpload event={effectiveEvent} hideNotifyGuests={true} />
        </>
      )}
    </>
  );
};

export default ManageVideoForm;
